import { H3 } from "@repo/ui/components/ui/typography"
import { cn } from "@repo/ui/lib/utils"
import { Check } from "lucide-react"
import * as React from "react"

type SelectableCardColor =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "destructive"
  | "info"

type SelectableCardVariant = "checkbox" | "radio"

interface SelectableCardContextValue {
  selected: boolean
  onSelect?: () => void
  color: SelectableCardColor
  variant: SelectableCardVariant
}

const SelectableCardContext = React.createContext<SelectableCardContextValue>({
  selected: false,
  color: "primary",
  variant: "checkbox",
})

interface SelectableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  onSelect?: () => void
  disabled?: boolean
  color?: SelectableCardColor
  variant?: SelectableCardVariant
  interactive?: boolean
}

const SelectableCard = React.forwardRef<HTMLDivElement, SelectableCardProps>(
  (
    {
      className,
      children,
      selected = false,
      onSelect,
      disabled,
      color = "primary",
      variant = "checkbox",
      interactive = true,
      ...props
    },
    ref,
  ) => {
    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (!disabled && interactive && onSelect) {
          e.stopPropagation()
          onSelect()
        }
      },
      [disabled, interactive, onSelect],
    )

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (
          !disabled &&
          interactive &&
          onSelect &&
          (e.key === "Enter" || e.key === " ")
        ) {
          e.preventDefault()
          onSelect()
        }
      },
      [disabled, interactive, onSelect],
    )

    const colorClasses = {
      primary: selected ? "border-primary bg-primary-background" : "",
      secondary: selected
        ? "border-secondary-foreground bg-secondary-background"
        : "",
      success: selected ? "border-success bg-success-background" : "",
      warning: selected ? "border-warning bg-warning-background" : "",
      destructive: selected
        ? "border-destructive bg-destructive-background"
        : "",
      info: selected ? "border-info-border bg-info-background" : "",
    }

    return (
      <SelectableCardContext.Provider
        value={{ selected, onSelect, color, variant }}
      >
        <div
          ref={ref}
          role={
            onSelect && interactive
              ? variant === "radio"
                ? "radio"
                : "checkbox"
              : undefined
          }
          tabIndex={onSelect && interactive && !disabled ? 0 : undefined}
          aria-checked={selected}
          aria-disabled={disabled}
          onClick={interactive ? handleClick : undefined}
          onKeyDown={interactive ? handleKeyDown : undefined}
          className={cn(
            "rounded-lg border bg-card text-card-foreground transition-all",
            onSelect && interactive && !disabled && "cursor-pointer",
            selected && variant === "radio" && interactive && "cursor-default",
            colorClasses[color],
            disabled && "opacity-50 cursor-not-allowed",
            {
              "cursor-default": !interactive,
            },
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </SelectableCardContext.Provider>
    )
  },
)
SelectableCard.displayName = "SelectableCard"

const SelectableCardIndicator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { selected, color, variant } = React.useContext(SelectableCardContext)

  const indicatorColors = {
    primary: "bg-primary",
    secondary: "bg-secondary-foreground",
    success: "bg-success",
    warning: "bg-warning",
    destructive: "bg-destructive",
    info: "bg-info-foreground",
  }

  return (
    <div
      ref={ref}
      className={cn(
        "ml-auto h-4 w-4 transition-all flex-shrink-0 flex justify-center items-center",
        variant === "radio" ? "rounded-full" : "rounded-sm",
        "border-2",
        selected && `${indicatorColors[color]} border-none`,
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      {selected && variant === "checkbox" && (
        <Check className="h-full w-full p-0.5 text-background" />
      )}
      {selected && variant === "radio" && (
        <div className="h-[6px] w-[6px] bg-background rounded-full" />
      )}
    </div>
  )
})
SelectableCardIndicator.displayName = "SelectableCardIndicator"

const SelectableCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-2 p-4 pb-2", className)} {...props} />
))
SelectableCardHeader.displayName = "SelectableCardHeader"

const SelectableCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <H3
    size="m"
    ref={ref}
    className={cn("leading-none tracking-tight text-foreground", className)}
    {...props}
  />
))
SelectableCardTitle.displayName = "SelectableCardTitle"

const SelectableCardLabel = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-sm font-semibold tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
))
SelectableCardLabel.displayName = "SelectableCardLabel"

const SelectableCardSpanLabel = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "text-sm font-semibold tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
))
SelectableCardSpanLabel.displayName = "SelectableCardSpanLabel"

const SelectableCardText = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-base font-base tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
))
SelectableCardText.displayName = "SelectableCardText"

const SelectableCardPrice = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-xl font-extrabold text-foreground", className)}
    {...props}
  />
))
SelectableCardPrice.displayName = "SelectableCardPrice"

const SelectableCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 pb-4 space-y-2", className)} {...props} />
))
SelectableCardContent.displayName = "SelectableCardContent"

const SelectableCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-foreground", className)}
    {...props}
  />
))
SelectableCardDescription.displayName = "SelectableCardDescription"

const SelectableCardSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-2", className)} {...props} />
))
SelectableCardSection.displayName = "SelectableCardList"

const SelectableCardListItems = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("pl-3 text-sm space-y-1", className)}
    {...props}
  />
))
SelectableCardListItems.displayName = "SelectableCardList"

const SelectableCardListItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, children, ...props }, ref) => (
  <li ref={ref} className={cn("flex items-start gap-2", className)} {...props}>
    <span className="mt-[0.575em] h-1 w-1 bg-current rounded-full flex-shrink-0" />
    <span>{children}</span>
  </li>
))
SelectableCardListItem.displayName = "SelectableCardListItem"

const SelectableCardIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-shrink-0 h-4 w-4 flex items-center justify-center text-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </div>
))
SelectableCardIcon.displayName = "SelectableCardIcon"

const SelectableCardBadge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
      "bg-secondary-foreground text-background",
      className,
    )}
    {...props}
  >
    {children}
  </div>
))
SelectableCardBadge.displayName = "SelectableCardBadge"

export {
  SelectableCard,
  SelectableCardIndicator,
  SelectableCardHeader,
  SelectableCardTitle,
  SelectableCardLabel,
  SelectableCardText,
  SelectableCardPrice,
  SelectableCardDescription,
  SelectableCardContent,
  SelectableCardSection,
  SelectableCardListItems,
  SelectableCardSpanLabel,
  SelectableCardListItem,
  SelectableCardIcon,
  SelectableCardBadge,
  type SelectableCardProps,
}
